// =============================================
// PortfolioPage.jsx - 내 자산 관리 페이지
// =============================================
// 구성 (위에서 아래로):
//   ① 상단 요약 카드 (총 평가금액 / 총 매수금액 / 총 손익 / 수익률)
//   ② 탭 [국내주식 | 해외주식 | 가상화폐]
//   ③ 보유 종목 테이블 (실시간 P&L 표시)
//   ④ 종목 추가 폼 (카테고리 + 거래소 + 티커 + 수량 + 평단가)
//   ⑤ 포트폴리오 가치 변화 라인 차트 (Recharts)
//
// 데이터 저장: 백엔드 SQLite DB (backend/assets.db)
//   → 브라우저를 껐다 켜도, 서버를 재시작해도 데이터 유지!
//   → 이전 방식(localStorage)과 달리 서버에 저장돼.
//
// ★ P&L 색상 (한국 주식 관례):
//   상승 → 빨간색 (RED)
//   하락 → 파란색 (BLUE)
//   보합 → 회색
// =============================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API_URL = 'http://localhost:8000';

// =============================================
// 상수 정의
// =============================================

// 탭 목록 — [전체]는 모든 카테고리를 합산해서 보여줘
const TABS = [
  { id: '전체',    label: '전체' },
  { id: '국내주식', label: '국내주식' },
  { id: '해외주식', label: '해외주식' },
  { id: '가상화폐', label: '가상화폐' },
];

// 히스토리 기간
const PERIODS = [
  { label: '1개월', value: '1mo' },
  { label: '3개월', value: '3mo' },
  { label: '1년',   value: '1y'  },
];


// =============================================
// 헬퍼 함수들
// =============================================

/**
 * 카테고리 + 거래소 → asset_type & currency 결정
 *
 * 이 정보는 백엔드가 현재가를 어디서 가져올지 결정하는 데 사용해:
 *   stock_kr   → yfinance (Yahoo Finance) KRW 기준
 *   stock_us   → yfinance (Yahoo Finance) USD 기준
 *   crypto_kr  → pyupbit (업비트) KRW 기준
 *   crypto_us  → 바이낸스 REST API USD 기준
 */
function deriveAssetMeta(category, exchange) {
  if (category === '국내주식') return { asset_type: 'stock_kr',  currency: 'KRW' };
  if (category === '해외주식') return { asset_type: 'stock_us',  currency: 'USD' };
  if (exchange  === '바이낸스') return { asset_type: 'crypto_us', currency: 'USD' };
  return                              { asset_type: 'crypto_kr', currency: 'KRW' };
}

/** 티커 입력 힌트 텍스트 */
function getTickerHint(category, exchange) {
  if (category === '국내주식') return '예: 005930.KS (삼성전자), 035420.KS (NAVER)';
  if (category === '해외주식') return '예: AAPL (애플), TSLA (테슬라), NVDA (엔비디아)';
  if (exchange  === '바이낸스') return '예: BTC, ETH, SOL  →  달러(USD) 기준 조회';
  return '예: BTC, ETH, XRP  →  원화(KRW) 기준 조회, 업비트';
}

/** P&L 색상: 한국 관례 — 상승=빨간색, 하락=파란색 */
function pnlColor(value) {
  if (value > 0) return '#f56565';   // 빨간색 (상승)
  if (value < 0) return '#63b3ed';   // 파란색 (하락)
  return '#a0aec0';                  // 회색 (보합)
}

/** 큰 숫자를 "1.2억", "3,456만" 형태로 축약 (차트 Y축용) */
function shortKrw(v) {
  if (v >= 100_000_000) return `${(v / 100_000_000).toFixed(1)}억`;
  if (v >= 10_000)      return `${Math.round(v / 10_000)}만`;
  return `₩${v.toLocaleString()}`;
}


// =============================================
// 메인 컴포넌트
// =============================================
export default function PortfolioPage() {

  // ── 데이터 상태 ──
  const [assets,        setAssets]        = useState([]);   // DB 자산 목록
  const [summaryItems,  setSummaryItems]  = useState({});   // P&L 결과 (id → item)
  const [summaryTotal,  setSummaryTotal]  = useState(null); // 전체 합산
  const [usdkrw,        setUsdkrw]        = useState(1350); // 달러/원 환율
  const [histData,      setHistData]      = useState([]);   // 차트 데이터
  const [histPeriod,    setHistPeriod]    = useState('1mo');

  // ── UI 상태 ──
  const [activeTab,      setActiveTab]      = useState('전체');   // 기본값: 전체
  const [showForm,       setShowForm]       = useState(false);
  const [loadingAssets,  setLoadingAssets]  = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingHist,    setLoadingHist]    = useState(false);

  // ── 추가 폼 상태 ──
  // purchase_date: 매수한 날짜 (YYYY-MM-DD). 히스토리 차트 시작점으로 사용돼.
  const [form, setForm] = useState({
    category:      '국내주식',
    exchange:      '업비트',   // 가상화폐 탭일 때만 사용
    ticker:        '',
    name:          '',
    amount:        '',
    buy_price:     '',
    purchase_date: new Date().toISOString().slice(0, 10),  // 기본값: 오늘
  });

  // =============================================
  // ① 환율 가져오기 (최초 1회)
  // =============================================
  useEffect(() => {
    axios.get(`${API_URL}/api/market/indices`)
      .then(r => {
        const rate = r.data?.usdkrw?.price;
        if (rate) setUsdkrw(rate);
      })
      .catch(() => {}); // 환율 실패 시 기본값 1350 유지
  }, []);


  // =============================================
  // ② 자산 목록 불러오기 (GET /api/my-assets)
  // =============================================
  const fetchAssets = useCallback(async () => {
    setLoadingAssets(true);
    try {
      const r = await axios.get(`${API_URL}/api/my-assets`);
      setAssets(r.data.assets || []);
    } catch (e) {
      console.error('자산 목록 조회 실패:', e);
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);


  // =============================================
  // ③ P&L 계산 (GET /api/my-assets/summary)
  // =============================================
  // ★ 이 API는 외부 서버(업비트, 바이낸스 등)에 요청을 보내서
  //   종목 수에 따라 수초가 걸릴 수 있어. 로딩 상태를 꼭 표시해줘.
  const fetchSummary = useCallback(async () => {
    if (assets.length === 0) {
      setSummaryItems({});
      setSummaryTotal(null);
      return;
    }
    setLoadingSummary(true);
    try {
      const r = await axios.get(`${API_URL}/api/my-assets/summary`, {
        params: { usdkrw },
      });
      // id → item 딕셔너리로 변환 (테이블에서 빠르게 찾기 위해)
      const map = {};
      (r.data.items || []).forEach(item => { map[item.id] = item; });
      setSummaryItems(map);
      setSummaryTotal(r.data.summary || null);
    } catch (e) {
      console.error('P&L 계산 실패:', e);
    } finally {
      setLoadingSummary(false);
    }
  }, [assets, usdkrw]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);


  // =============================================
  // ④ 포트폴리오 히스토리 (Recharts 차트용)
  // =============================================
  // ★ 탭 연동 흐름:
  //   ① 사용자가 탭 클릭 → activeTab 상태 변경
  //   ② React가 fetchHistory를 다시 실행 (activeTab이 의존성에 포함되어 있어)
  //   ③ category 파라미터를 백엔드에 전달
  //      - '전체' → category="" → 모든 자산 합산
  //      - '국내주식' → category="국내주식" → 국내주식만 계산
  //      - '해외주식' / '가상화폐' → 각 카테고리만 계산
  //   ④ 백엔드가 해당 카테고리의 일별 가치를 합산해서 반환
  //   ⑤ Recharts 차트가 새 데이터로 업데이트돼
  const fetchHistory = useCallback(async () => {
    if (assets.length === 0) { setHistData([]); return; }
    setLoadingHist(true);
    try {
      const r = await axios.get(`${API_URL}/api/my-assets/history`, {
        params: {
          period:   histPeriod,
          usdkrw,
          // '전체' 탭이면 category를 비워서 전체 합산, 아니면 해당 카테고리만 필터
          category: activeTab === '전체' ? '' : activeTab,
        },
      });
      setHistData(r.data.data || []);
    } catch (e) {
      console.error('히스토리 조회 실패:', e);
      setHistData([]);
    } finally {
      setLoadingHist(false);
    }
  // activeTab이 바뀔 때마다 차트를 새로 계산해야 해 → 의존성에 추가
  }, [assets, histPeriod, usdkrw, activeTab]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);


  // =============================================
  // ⑤ 자산 추가 (POST /api/my-assets)
  // =============================================
  const handleAdd = async () => {
    if (!form.ticker || !form.name || !form.amount || !form.buy_price) {
      alert('모든 항목을 입력해줘!');
      return;
    }
    if (Number(form.amount) <= 0 || Number(form.buy_price) <= 0) {
      alert('수량과 매수가는 0보다 커야 해!');
      return;
    }

    const { asset_type, currency } = deriveAssetMeta(form.category, form.exchange);

    try {
      await axios.post(`${API_URL}/api/my-assets`, {
        category:      form.category,
        ticker:        form.ticker.trim().toUpperCase(),
        name:          form.name.trim(),
        amount:        parseFloat(form.amount),
        buy_price:     parseFloat(form.buy_price),
        currency,
        asset_type,
        purchase_date: form.purchase_date || new Date().toISOString().slice(0, 10),
      });
      // 폼 초기화 후 목록 새로고침
      setForm({
        category: '국내주식', exchange: '업비트',
        ticker: '', name: '', amount: '', buy_price: '',
        purchase_date: new Date().toISOString().slice(0, 10),
      });
      setShowForm(false);
      await fetchAssets();
    } catch (e) {
      alert(e.response?.data?.detail || '추가 중 오류가 발생했어.');
    }
  };


  // =============================================
  // ⑥ 자산 삭제 (DELETE /api/my-assets/{id})
  // =============================================
  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}"을(를) 삭제할까? 복구할 수 없어!`)) return;
    try {
      await axios.delete(`${API_URL}/api/my-assets/${id}`);
      await fetchAssets(); // 목록 새로고침
    } catch (e) {
      alert('삭제 중 오류가 발생했어.');
    }
  };


  // ── 현재 탭의 자산만 필터링 ──
  // '전체' 탭이면 모든 자산을 보여주고, 나머지 탭은 해당 카테고리만 필터링해
  const tabAssets = activeTab === '전체'
    ? assets
    : assets.filter(a => a.category === activeTab);


  // =============================================
  // 렌더링
  // =============================================
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* ─ 페이지 제목 ─ */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: '1.3rem' }}>내 자산</h2>
        <span style={{ fontSize: '0.75rem', color: '#4a5568' }}>
          데이터 저장: SQLite DB (backend/assets.db)
        </span>
      </div>

      {/* =============================================
          섹션 1: 전체 요약 카드
          ============================================= */}
      {summaryTotal ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '0.75rem',
        }}>
          <SummaryCard
            label="총 평가금액"
            value={`₩${summaryTotal.total_current_krw.toLocaleString()}`}
            sub="현재가 기준 (KRW 환산)"
          />
          <SummaryCard
            label="총 매수금액"
            value={`₩${summaryTotal.total_invested_krw.toLocaleString()}`}
            sub="평단가 기준 (KRW 환산)"
          />
          <SummaryCard
            label="총 손익"
            value={`${summaryTotal.total_profit_krw >= 0 ? '+' : ''}₩${Math.abs(summaryTotal.total_profit_krw).toLocaleString()}`}
            color={pnlColor(summaryTotal.total_profit_krw)}
          />
          <SummaryCard
            label="전체 수익률"
            value={`${summaryTotal.total_profit_pct >= 0 ? '+' : ''}${summaryTotal.total_profit_pct}%`}
            color={pnlColor(summaryTotal.total_profit_pct)}
            large
          />
        </div>
      ) : loadingSummary ? (
        <div style={{
          padding: '1rem',
          backgroundColor: '#1a202c',
          borderRadius: '10px',
          color: '#718096',
          fontSize: '0.85rem',
          border: '1px solid #2d3748',
        }}>
          실시간 수익률 계산 중... 외부 API 호출 중이야, 잠깐 기다려줘!
        </div>
      ) : assets.length === 0 ? (
        <div style={{
          padding: '1.5rem',
          backgroundColor: '#1a202c',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#718096',
          border: '1px solid #2d3748',
        }}>
          종목을 추가하면 전체 수익률 요약이 여기 표시돼!
        </div>
      ) : null}

      {/* =============================================
          섹션 2: 탭 + 보유 종목 테이블
          ============================================= */}
      <div>

        {/* 탭 헤더 + 새로고침/추가 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}>
          {/* 탭 버튼 */}
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '0.4rem 1rem',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 'bold',
                  backgroundColor: activeTab === tab.id ? '#3182ce' : '#2d3748',
                  color: activeTab === tab.id ? 'white' : '#a0aec0',
                  transition: 'background-color 0.15s',
                }}
              >
                {tab.label}
                {/* 탭별 종목 수 표시 — '전체'는 전체 개수, 나머지는 해당 카테고리 수 */}
                <span style={{ marginLeft: '4px', fontSize: '0.72rem', opacity: 0.75 }}>
                  ({tab.id === '전체' ? assets.length : assets.filter(a => a.category === tab.id).length})
                </span>
              </button>
            ))}
          </div>

          {/* 액션 버튼들 */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={fetchSummary}
              disabled={loadingSummary || assets.length === 0}
              style={outlineBtn}
            >
              {loadingSummary ? '계산 중...' : '수익률 새로고침'}
            </button>
            <button
              onClick={() => setShowForm(v => !v)}
              style={primaryBtn}
            >
              {showForm ? '폼 닫기' : '+ 종목 추가'}
            </button>
          </div>
        </div>

        {/* ── 종목 추가 폼 ── */}
        {showForm && (
          <AddForm
            form={form}
            setForm={setForm}
            onSubmit={handleAdd}
            onCancel={() => setShowForm(false)}
          />
        )}

        {/* ── 보유 종목 테이블 ── */}
        {loadingAssets ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#718096' }}>
            자산 목록 불러오는 중...
          </div>
        ) : tabAssets.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#1a202c',
            borderRadius: '12px',
            border: '1px solid #2d3748',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📭</div>
            <div style={{ color: '#a0aec0' }}>
              {activeTab} 종목이 없어. 위의 '+ 종목 추가'를 눌러봐!
            </div>
          </div>
        ) : (
          <AssetTable
            assets={tabAssets}
            summaryItems={summaryItems}
            loadingSummary={loadingSummary}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* =============================================
          섹션 3: 포트폴리오 가치 변화 차트 (Recharts)
          ============================================= */}
      {assets.length > 0 && (
        <div style={{
          backgroundColor: '#1a202c',
          borderRadius: '12px',
          padding: '1.25rem',
          border: '1px solid #2d3748',
        }}>
          {/* 차트 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}>
            <div>
              <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: '1rem' }}>
                포트폴리오 가치 변화
                {/* 현재 보고 있는 탭을 배지로 표시 → 어떤 데이터인지 바로 알 수 있어 */}
                <span style={{
                  marginLeft: '8px',
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  backgroundColor: activeTab === '전체' ? '#2c5282' : '#1a365d',
                  color: '#90cdf4',
                  fontWeight: 'normal',
                }}>
                  {activeTab}
                </span>
              </h3>
              <div style={{ fontSize: '0.72rem', color: '#4a5568', marginTop: '2px' }}>
                {activeTab === '전체'
                  ? '전체 자산 합산 · 총 평가금액과 일치해야 해'
                  : `${activeTab} 카테고리만 · 원화(KRW) 환산 기준`}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.3rem' }}>
              {PERIODS.map(p => (
                <button
                  key={p.value}
                  onClick={() => setHistPeriod(p.value)}
                  style={{
                    padding: '0.25rem 0.6rem',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.78rem',
                    backgroundColor: histPeriod === p.value ? '#553c9a' : '#2d3748',
                    color: histPeriod === p.value ? '#e9d8fd' : '#718096',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <PortfolioChart data={histData} loading={loadingHist} />
        </div>
      )}
    </div>
  );
}


// =============================================
// 종목 추가 폼 컴포넌트
// =============================================
function AddForm({ form, setForm, onSubmit, onCancel }) {
  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const isCrypto   = form.category === '가상화폐';
  const isUSD      = form.category === '해외주식' || (isCrypto && form.exchange === '바이낸스');
  const priceLabel = isUSD ? '평균 매수가 (USD, $)' : '평균 매수가 (원, ₩)';
  const tickerHint = getTickerHint(form.category, form.exchange);

  return (
    <div style={{
      backgroundColor: '#1a202c',
      borderRadius: '12px',
      padding: '1.25rem',
      marginBottom: '1rem',
      border: '1px solid #3182ce',  // 파란 테두리로 강조
    }}>
      <h4 style={{ margin: '0 0 1rem', color: '#e2e8f0' }}>종목 추가</h4>

      {/* 카테고리 선택 */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={labelStyle}>카테고리 선택</label>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '4px' }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => upd('category', t.id)}
              style={{
                padding: '0.3rem 0.8rem',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.82rem',
                backgroundColor: form.category === t.id ? '#3182ce' : '#2d3748',
                color:           form.category === t.id ? 'white'   : '#a0aec0',
                transition: 'background-color 0.15s',
              }}
            >
              {t.id}
            </button>
          ))}
        </div>
      </div>

      {/* 거래소 선택 — 가상화폐일 때만 표시 */}
      {isCrypto && (
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>거래소 (현재가 조회 방식)</label>
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '4px' }}>
            {['업비트', '바이낸스'].map(ex => (
              <button
                key={ex}
                onClick={() => upd('exchange', ex)}
                style={{
                  padding: '0.3rem 0.9rem',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.82rem',
                  backgroundColor: form.exchange === ex ? '#38a169' : '#2d3748',
                  color:           form.exchange === ex ? 'white'   : '#a0aec0',
                  transition: 'background-color 0.15s',
                }}
              >
                {ex} {ex === '업비트' ? '(₩ 원화)' : '($ 달러)'}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#4a5568', marginTop: '4px' }}>
            업비트: 현재가를 원화(KRW)로 조회 / 바이낸스: 달러(USD)로 조회
          </div>
        </div>
      )}

      {/* 입력 필드 2열 그리드 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>

        {/* 티커 */}
        <div>
          <label style={labelStyle}>티커 (종목 코드)</label>
          <input
            style={inputStyle}
            value={form.ticker}
            onChange={e => upd('ticker', e.target.value)}
            placeholder={isCrypto ? 'BTC, ETH, XRP...' : form.category === '국내주식' ? '005930.KS' : 'AAPL, TSLA'}
          />
          <div style={{ fontSize: '0.68rem', color: '#4a5568', marginTop: '3px' }}>
            {tickerHint}
          </div>
        </div>

        {/* 종목명 */}
        <div>
          <label style={labelStyle}>종목명</label>
          <input
            style={inputStyle}
            value={form.name}
            onChange={e => upd('name', e.target.value)}
            placeholder="예: 삼성전자, 애플, 비트코인"
          />
        </div>

        {/* 보유 수량 */}
        <div>
          <label style={labelStyle}>보유 수량</label>
          <input
            style={inputStyle}
            type="number"
            min="0"
            step="any"
            value={form.amount}
            onChange={e => upd('amount', e.target.value)}
            placeholder="예: 10, 0.5, 100"
          />
        </div>

        {/* 평균 매수가 */}
        <div>
          <label style={labelStyle}>{priceLabel}</label>
          <input
            style={inputStyle}
            type="number"
            min="0"
            step="any"
            value={form.buy_price}
            onChange={e => upd('buy_price', e.target.value)}
            placeholder={isUSD ? '예: 150.50' : '예: 70000, 60000000'}
          />
        </div>

        {/* 매수 날짜 ★ 전술 A 핵심 — 이 날짜부터 포트폴리오 차트가 시작돼! */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>
            매수 날짜&nbsp;
            <span style={{ color: '#4a5568', fontWeight: 'normal' }}>
              (이 날짜부터 포트폴리오 가치 변화 차트가 시작돼)
            </span>
          </label>
          <input
            style={{ ...inputStyle, width: 'auto', minWidth: '180px' }}
            type="date"
            value={form.purchase_date}
            max={new Date().toISOString().slice(0, 10)}  // 오늘 이후는 선택 불가
            onChange={e => upd('purchase_date', e.target.value)}
          />
        </div>
      </div>

      {/* 버튼 */}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        <button onClick={onSubmit} style={{ ...primaryBtn, flex: 1, padding: '0.6rem' }}>
          추가하기
        </button>
        <button onClick={onCancel} style={outlineBtn}>
          취소
        </button>
      </div>
    </div>
  );
}


// =============================================
// 자산 테이블 컴포넌트
// =============================================
function AssetTable({ assets, summaryItems, loadingSummary, onDelete }) {
  const HEADERS = ['종목', '수량', '평단가', '현재가', '평가금액', '손익', '수익률', ''];

  return (
    <div style={{ backgroundColor: '#1a202c', borderRadius: '12px', overflow: 'hidden', border: '1px solid #2d3748' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e2e8f0', fontSize: '0.87rem' }}>
        <thead>
          <tr style={{ backgroundColor: '#2d3748' }}>
            {HEADERS.map((h, i) => (
              <th key={i} style={{
                padding: '10px 12px',
                textAlign: (i >= 2 && i <= 6) ? 'right' : 'left',
                color: '#718096',
                fontSize: '0.76rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {assets.map(asset => {
            const pnl = summaryItems[asset.id];
            return (
              <AssetRow
                key={asset.id}
                asset={asset}
                pnl={pnl}
                loadingSummary={loadingSummary && !pnl}
                onDelete={() => onDelete(asset.id, asset.name)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// =============================================
// 자산 테이블 한 줄 컴포넌트
// =============================================
function AssetRow({ asset, pnl, loadingSummary, onDelete }) {
  const isUSD  = asset.currency === 'USD';
  const cur    = isUSD ? '$' : '₩';
  const digits = isUSD ? 4 : 0;  // 해외주식·코인USD는 소수점 표시

  // 숫자 포맷 헬퍼
  const fmt = (v, d = digits) =>
    v != null
      ? `${cur}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d })}`
      : '-';

  const profit  = pnl?.profit_loss ?? null;
  const color   = profit !== null ? pnlColor(profit) : '#a0aec0';

  return (
    <tr style={{
      borderBottom: '1px solid #2d3748',
      transition: 'background-color 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1e2533'}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
    >
      {/* 종목명 + 분류 태그 + 매수일 */}
      <td style={tdStyle}>
        <div style={{ fontWeight: 'bold', color: '#e2e8f0' }}>{asset.ticker}</div>
        <div style={{ fontSize: '0.74rem', color: '#718096' }}>{asset.name}</div>
        <div style={{ fontSize: '0.67rem', color: '#4a5568', marginTop: '1px' }}>
          {asset.asset_type === 'crypto_kr' && '업비트 (₩)'}
          {asset.asset_type === 'crypto_us' && '바이낸스 ($)'}
          {asset.asset_type === 'stock_kr'  && '국내주식 (₩)'}
          {asset.asset_type === 'stock_us'  && '해외주식 ($)'}
          {asset.purchase_date && (
            <span style={{ marginLeft: '4px' }}>· 매수 {asset.purchase_date}</span>
          )}
        </div>
      </td>

      {/* 수량 */}
      <td style={tdStyle}>
        {Number(asset.amount).toLocaleString('en-US', { maximumFractionDigits: 8 })}
      </td>

      {/* 평단가 */}
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {fmt(asset.buy_price)}
      </td>

      {/* 현재가 */}
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {loadingSummary ? (
          <span style={{ color: '#4a5568', fontSize: '0.78rem' }}>조회 중...</span>
        ) : pnl?.error ? (
          <span style={{ color: '#fc8181', fontSize: '0.75rem' }}>조회 실패</span>
        ) : (
          fmt(pnl?.current_price)
        )}
      </td>

      {/* 평가금액 */}
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {fmt(pnl?.current_value)}
      </td>

      {/* 손익 */}
      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color }}>
        {profit !== null
          ? `${profit >= 0 ? '+' : ''}${fmt(profit)}`
          : '-'}
      </td>

      {/* 수익률 배지 */}
      <td style={{ ...tdStyle, textAlign: 'right' }}>
        {pnl?.profit_pct != null ? (
          <span style={{
            color,
            fontWeight: 'bold',
            padding: '2px 8px',
            borderRadius: '5px',
            fontSize: '0.82rem',
            backgroundColor:
              pnl.profit_pct > 0 ? 'rgba(245,101,101,0.12)' :
              pnl.profit_pct < 0 ? 'rgba(99,179,237,0.12)' :
              '#2d3748',
          }}>
            {pnl.profit_pct >= 0 ? '+' : ''}{pnl.profit_pct}%
          </span>
        ) : '-'}
      </td>

      {/* 삭제 버튼 */}
      <td style={tdStyle}>
        <button
          onClick={onDelete}
          title="삭제"
          style={{
            background: 'none',
            border: 'none',
            color: '#4a5568',
            cursor: 'pointer',
            fontSize: '1rem',
            padding: '2px 6px',
            borderRadius: '4px',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#fc8181'}
          onMouseLeave={e => e.currentTarget.style.color = '#4a5568'}
        >
          ✕
        </button>
      </td>
    </tr>
  );
}


// =============================================
// Recharts 라인 차트 — 포트폴리오 가치 변화
// =============================================
function PortfolioChart({ data, loading }) {

  if (loading) {
    return (
      <div style={{
        height: '260px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#4a5568',
        fontSize: '0.88rem',
      }}>
        차트 데이터 계산 중... (시간이 조금 걸릴 수 있어)
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div style={{
        height: '260px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#4a5568',
        fontSize: '0.88rem',
        flexDirection: 'column',
        gap: '0.4rem',
      }}>
        <span>차트 데이터가 없어</span>
        <span style={{ fontSize: '0.75rem' }}>
          종목을 추가하면 자동으로 계산돼 (주말·장 마감 시 데이터 없을 수 있어)
        </span>
      </div>
    );
  }

  // Y축 포매터: 큰 숫자를 "1.2억 / 3,456만" 형태로 축약
  const yFmt = (v) => shortKrw(v);

  // 툴팁 포매터
  const tooltipFmt = (value) => [
    `₩${Number(value).toLocaleString()}`,
    '포트폴리오 가치',
  ];

  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart
        data={data}
        margin={{ top: 5, right: 25, left: 15, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />

        {/* X축: 날짜 (YYYY-MM-DD → MM/DD 축약) */}
        <XAxis
          dataKey="time"
          tick={{ fill: '#718096', fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: '#4a5568' }}
          tickFormatter={(v) => v?.slice(5)}  // "2024-03-01" → "03/01"
        />

        {/* Y축: 원화 금액 (만/억 단위) */}
        <YAxis
          tick={{ fill: '#718096', fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          tickFormatter={yFmt}
          width={70}
        />

        {/* 툴팁 */}
        <Tooltip
          contentStyle={{
            backgroundColor: '#2d3748',
            border: 'none',
            borderRadius: '8px',
            color: '#e2e8f0',
          }}
          labelStyle={{ color: '#a0aec0', fontSize: '0.8rem' }}
          formatter={tooltipFmt}
        />

        {/* 라인 */}
        <Line
          type="monotone"
          dataKey="value"
          stroke="#3182ce"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4, fill: '#63b3ed', strokeWidth: 0 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}


// =============================================
// 요약 카드 컴포넌트
// =============================================
function SummaryCard({ label, value, color, sub, large }) {
  return (
    <div style={{
      backgroundColor: '#1a202c',
      borderRadius: '10px',
      padding: '1rem',
      border: '1px solid #2d3748',
    }}>
      <div style={{ color: '#718096', fontSize: '0.75rem', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{
        fontSize:   large ? '1.7rem' : '1.3rem',
        fontWeight: 'bold',
        color:      color || '#e2e8f0',
        lineHeight: 1.2,
        wordBreak: 'break-all',
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ color: '#4a5568', fontSize: '0.68rem', marginTop: '3px' }}>
          {sub}
        </div>
      )}
    </div>
  );
}


// =============================================
// 스타일 상수
// =============================================
const primaryBtn = {
  padding:         '0.45rem 1rem',
  backgroundColor: '#3182ce',
  color:           'white',
  border:          'none',
  borderRadius:    '6px',
  cursor:          'pointer',
  fontWeight:      'bold',
  fontSize:        '0.85rem',
};

const outlineBtn = {
  padding:         '0.45rem 0.9rem',
  backgroundColor: 'transparent',
  color:           '#a0aec0',
  border:          '1px solid #4a5568',
  borderRadius:    '6px',
  cursor:          'pointer',
  fontSize:        '0.85rem',
};

const labelStyle = {
  fontSize:      '0.76rem',
  color:         '#718096',
  display:       'block',
  marginBottom:  '4px',
};

const inputStyle = {
  width:           '100%',
  padding:         '0.45rem 0.75rem',
  backgroundColor: '#2d3748',
  border:          '1px solid #4a5568',
  borderRadius:    '6px',
  color:           '#e2e8f0',
  fontSize:        '0.88rem',
  boxSizing:       'border-box',
  outline:         'none',
};

const tdStyle = {
  padding:       '10px 12px',
  verticalAlign: 'middle',
};
